/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { Children, useCallback, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

const DIRECTIONS = {
    FORWARD: 1,
    BACKWARD: -1,
} as const;

interface SwatchChild {
    props?: {
        value?: string;
        handleSelect?: (value: string) => void;
        selected?: boolean;
        isFocusable?: boolean;
        shape?: 'color' | 'label' | 'image';
        disabled?: boolean;
    };
}

/**
 * Props for the SwatchGroup component
 */
interface SwatchGroupProps {
    /** Display name shown next to the label */
    displayName?: string;
    /** Swatch components to render within the group */
    children: React.ReactNode;
    /** Label text displayed above the swatches */
    label?: string;
    /** Currently selected swatch value */
    value?: string;
    /** Callback function called when a swatch is selected. */
    handleChange?: (value: string) => void;
    /**
     * When true, suppress the internal `{label}: {displayName}` header — the caller supplies the header
     * elsewhere (e.g. a CollapsibleSection summary). The radiogroup stays labeled via `aria-label`.
     */
    hideHeader?: boolean;
    /** Additional CSS classes to apply to the container */
    className?: string;
}

const noop = (..._args: unknown[]): void => {
    // Intentionally empty - default no-op function for handleChange
};

/**
 * A container component that manages a group of swatch components with keyboard navigation and selection.
 *
 * Features:
 * - Keyboard navigation with arrow keys (wraps around at start/end)
 * - Accessible radio group implementation
 * - Automatic focus management
 * - Customizable labels and styling
 *
 * @example
 * ```tsx
 * <SwatchGroup
 *   label="Color"
 *   value={selectedColor}
 *   handleChange={setSelectedColor}
 * >
 *   <Swatch value="red" mode="hover">Red</Swatch>
 *   <Swatch value="blue" mode="hover">Blue</Swatch>
 * </SwatchGroup>
 * ```
 */
export const SwatchGroup: React.FC<SwatchGroupProps> = ({
    displayName,
    children,
    label = '',
    value,
    handleChange = noop,
    hideHeader = false,
    className,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const labelId = `swatch-group-label-${React.useId()}`;

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const { key } = e;
            const childrenArray = Children.toArray(children);

            const move = (direction: typeof DIRECTIONS.FORWARD | typeof DIRECTIONS.BACKWARD = DIRECTIONS.FORWARD) => {
                // Find the currently focused element in the DOM to get accurate starting position
                let currentIndex = 0;
                if (typeof document !== 'undefined' && wrapperRef.current) {
                    const focusedElement = document.activeElement;
                    const elementIndex = Array.from(wrapperRef.current.children).findIndex(
                        (child) => child === focusedElement
                    );
                    if (elementIndex !== -1) {
                        currentIndex = elementIndex;
                    }
                }

                let index = currentIndex + direction;

                // Handle wrapping
                if (index >= childrenArray.length) {
                    index = 0; // Wrap to beginning
                } else if (index < 0) {
                    index = childrenArray.length - 1; // Wrap to end
                }

                const swatchEl = wrapperRef?.current?.children[index] as HTMLElement;

                // Call handleChange when navigating with keyboard
                const newChildElement = childrenArray[index] as React.ReactElement<SwatchChild['props']>;
                const newValue = newChildElement?.props?.value;
                const isDisabled = newChildElement?.props?.disabled;
                if (newValue && !isDisabled) {
                    handleChange(newValue);
                }

                swatchEl?.focus();
            };

            switch (key) {
                case 'ArrowUp':
                case 'ArrowLeft':
                    e.preventDefault();
                    userInteractedRef.current = true;
                    move(DIRECTIONS.BACKWARD);
                    break;
                case 'ArrowDown':
                case 'ArrowRight':
                    e.preventDefault();
                    userInteractedRef.current = true;
                    move(DIRECTIONS.FORWARD);
                    break;
                default:
                    break;
            }
        },
        // do not need handleChange as dep
        // oxlint-disable-next-line react-hooks/exhaustive-deps
        [children]
    );

    const containerClasses = cn('space-y-3', className);

    const labelClasses = 'flex items-center gap-2 text-base font-semibold leading-6 text-card-foreground';

    // Check if this is a square swatch group (size, material, image tiles, etc.). Both text
    // `label` tiles and `image` tiles use the wrapped, boxed tile layout; only the color pill
    // group uses the inline pill layout.
    const firstChildShape = (React.Children.toArray(children)[0] as React.ReactElement<SwatchChild['props']>)?.props
        ?.shape;
    const isSquareSwatchGroup = firstChildShape === 'label' || firstChildShape === 'image';

    const swatchesWrapperClasses = isSquareSwatchGroup
        ? 'inline-flex flex-wrap gap-2 focus:outline-none bg-swatch-group-bg p-1'
        : 'flex flex-wrap gap-[var(--swatch-pill-gap,0.5rem)] focus:outline-none';

    // Exactly one swatch carries the group's tabstop (roving tabindex). Prefer the
    // selected swatch, but a disabled swatch forces its own tabIndex to -1, so if the
    // selected value maps to a disabled swatch (e.g. an out-of-stock variant named in
    // the URL) the tabstop would vanish and the whole group become keyboard-unreachable.
    // Fall back to the first enabled swatch in that case, then to index 0.
    const childArray = Children.toArray(children) as React.ReactElement<SwatchChild['props']>[];
    const selectedIndex = value ? childArray.findIndex((c) => c.props?.value === value) : -1;
    const firstEnabledIndex = childArray.findIndex((c) => !c.props?.disabled);
    const focusableIndex =
        selectedIndex !== -1 && !childArray[selectedIndex].props?.disabled
            ? selectedIndex
            : firstEnabledIndex !== -1
              ? firstEnabledIndex
              : 0;

    const prevValueRef = useRef(value);
    const userInteractedRef = useRef(false);
    useEffect(() => {
        if (value && value !== prevValueRef.current && userInteractedRef.current && wrapperRef.current) {
            const el = wrapperRef.current.children[focusableIndex] as HTMLElement | undefined;
            // Arrow-key navigation already focuses the swatch synchronously in move(); only
            // move focus here when it isn't already on the target (the click/navigation path),
            // so a keyboard selection doesn't focus twice.
            if (el && el !== document.activeElement) {
                requestAnimationFrame(() => el.focus());
            }
            userInteractedRef.current = false;
        }
        prevValueRef.current = value;
    }, [value, focusableIndex]);

    return (
        // oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- radiogroup composite widget: arrow-key roving handled at group level per ARIA APG
        <div
            className={containerClasses}
            onKeyDown={onKeyDown}
            onClick={(e) => {
                // Only arm the post-selection focus restore for keyboard activation. A
                // keyboard-driven click (Enter/Space) reports detail === 0; a real mouse click
                // reports detail >= 1. This keeps a stray pointer click from leaving the flag set
                // so a later external value change can't steal focus.
                if (e.detail === 0) {
                    userInteractedRef.current = true;
                }
            }}>
            <div
                className={isSquareSwatchGroup ? 'inline-flex flex-col gap-3' : 'flex flex-col gap-3'}
                role="radiogroup"
                aria-labelledby={!hideHeader && label ? labelId : undefined}
                aria-label={hideHeader ? label : undefined}>
                {!hideHeader && label && (
                    <div id={labelId} className={labelClasses}>
                        <span>{label}:</span>
                        {displayName && <span>{displayName}</span>}
                    </div>
                )}
                <div ref={wrapperRef} className={swatchesWrapperClasses} data-slot="swatch-container">
                    {childArray.map((childElement, index) => {
                        const selected = childElement.props?.value === value;

                        return React.cloneElement(childElement, {
                            key: childElement.props?.value || index,
                            handleSelect: handleChange,
                            selected,
                            isFocusable: index === focusableIndex,
                        });
                    })}
                </div>
            </div>
        </div>
    );
};

SwatchGroup.displayName = 'SwatchGroup';
